import {
  UndeliverCouponsResponse,
  undeliverCoupons,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  DELIVERY_CHANNEL_CONFIG,
  DeliveryChannel,
  GiftCouponForRedemption,
} from '@shared/api/giftCoupon/giftCoupon.interface';

/**
 * 발송완료 취소 실행 — 사유 입력·확인 UI는 UndeliverModal이 담당하고,
 * 여기는 호출 + 실패 안내(404 = 구서버)만 맡는다. 실패 시 null.
 * 스펙: docs/gift-coupon-undeliver-spec.md
 */
export async function executeUndeliver(
  couponIds: string[],
  reason: string,
): Promise<UndeliverCouponsResponse | null> {
  try {
    return await undeliverCoupons(couponIds, reason);
  } catch (error) {
    const e = error as {
      response?: { status?: number; data?: { message?: string } };
    };
    if (e?.response?.status === 404) {
      alert('발송취소: 서버 업데이트 후 사용 가능');
    } else {
      // 정산서 포함·비Delivered 등 서버 전체검증 메시지를 그대로 노출
      alert(e?.response?.data?.message || '발송취소 실패');
    }
    return null;
  }
}

/** 완료 토스트 문구 — 키워드 한 줄 (예: "발송취소 3건 · 환급 +25,500원") */
export function formatUndeliverToast(result: UndeliverCouponsResponse): string {
  const restoredSum = result.restored.reduce((sum, r) => sum + r.amount, 0);
  const restoredText =
    restoredSum > 0 ? `환급 +${restoredSum.toLocaleString()}원` : '환급 없음';
  return `발송취소 ${result.undelivered}건 · ${restoredText}`;
}

export interface UndeliverChannelEstimate {
  /** null = 매체 미입력 (환급 없음) — 원장 잔액 대조도 불가 */
  channel: DeliveryChannel | null;
  label: string;
  count: number;
  sum: number;
  isUnknown: boolean;
}

/** 모달 요약용 채널별 환급 예상 — 실제 환급은 서버가 원장 기장분에 한해 수행 */
export function estimateUndeliverByChannel(
  coupons: GiftCouponForRedemption[],
): UndeliverChannelEstimate[] {
  const byChannel = new Map<string, UndeliverChannelEstimate>();
  for (const c of coupons) {
    const ch = c.gifticon?.deliveryChannel;
    const label = ch ? DELIVERY_CHANNEL_CONFIG[ch]?.label ?? ch : '미지정';
    const entry = byChannel.get(label) ?? {
      channel: ch ?? null,
      label,
      count: 0,
      sum: 0,
      isUnknown: !ch,
    };
    entry.count++;
    entry.sum += c.gifticon?.priceWhole ?? 0;
    byChannel.set(label, entry);
  }
  // 미지정(환급 불가)은 맨 뒤로
  return Array.from(byChannel.values()).sort(
    (a, b) => Number(a.isUnknown) - Number(b.isUnknown),
  );
}
